/**
 * EmailSettingsPage — Configuration SMTP (PlanetHoster ou autre)
 *
 * Permet à l'administrateur de :
 *  - Saisir les paramètres SMTP (host, port, username, password, sender)
 *  - Activer/désactiver l'envoi global
 *  - Envoyer un email de test pour valider la configuration
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Mail, Save, Loader2, Send, ArrowLeft, Server, Lock, User,
  Eye, EyeOff, CheckCircle2, AlertCircle, Power, Info,
  RefreshCw, Search, Inbox, Clock as ClockIcon, X,
} from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
const API = process.env.REACT_APP_BACKEND_URL;

const EmailSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    provider: 'smtp',
    host: '',
    port: 465,
    username: '',
    password: '',
    sender_email: 'noreply@sfo-alc.fr',
    sender_name: 'SFOALC',
    use_ssl: true,
    use_starttls: false,
    has_password: false,
  });
  const [testEmail, setTestEmail] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/admin/email-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(c => ({ ...c, ...data, password: '' }));
      }
    } catch {
      toast.error('Erreur de chargement');
    }
    setLoading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...config };
      // Don't send empty password (keeps existing)
      if (!payload.password) delete payload.password;
      const res = await fetch(`${API}/api/admin/email-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('save failed');
      const data = await res.json();
      setConfig(c => ({ ...c, ...data, password: '' }));
      toast.success('Configuration SMTP enregistrée');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testEmail) {
      toast.error('Saisissez une adresse de test');
      return;
    }
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/admin/email-settings/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: testEmail }),
      });
      const data = await res.json();
      if (data.ok) {
        toast.success(`Email de test envoyé à ${testEmail} ✓`);
      } else {
        toast.error(data.message || `Échec : ${data.error || data.reason || 'inconnu'}`);
      }
    } catch (e) {
      toast.error('Erreur réseau lors du test');
    }
    setTesting(false);
  };

  const checkConnection = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const { host, port, username, password, use_ssl, use_starttls } = config;
      const res = await fetch(`${API}/api/admin/email-settings/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ host, port, username, password, use_ssl, use_starttls }),
      });
      setCheckResult(await res.json());
    } catch {
      setCheckResult({ ok: false, message: 'Erreur réseau : impossible de joindre la plateforme.' });
    }
    setChecking(false);
  };

  // Auto-update port/SSL based on common presets
  const setPortPreset = (port) => {
    setConfig(c => ({
      ...c,
      port,
      use_ssl: port === 465,
      use_starttls: port === 587,
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 size={32} className="animate-spin text-[#35AFE4]" />
        </div>
      </AdminLayout>
    );
  }

  const statusColor = config.enabled && config.has_password && config.host ? 'success' : 'warning';

  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 py-6 max-w-3xl" data-testid="email-settings-page">

        {/* Header */}
        <div className="mb-6">
          <button onClick={() => navigate('/admin')} className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 mb-2">
            <ArrowLeft size={14} /> Tableau de bord
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-1.5" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Configuration des emails
          </h1>
          <p className="text-sm text-gray-600">
            Connectez votre serveur SMTP (PlanetHoster, OVH, Gmail…) pour envoyer les emails transactionnels.
          </p>
        </div>

        {/* Status banner */}
        <div className={`rounded-xl p-4 mb-6 border ${
          statusColor === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
        }`} data-testid="status-banner">
          <div className="flex items-center gap-3">
            {statusColor === 'success'
              ? <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0" />
              : <AlertCircle size={20} className="text-amber-600 flex-shrink-0" />}
            <div className="flex-1">
              <p className={`text-sm font-bold ${statusColor === 'success' ? 'text-emerald-900' : 'text-amber-900'}`}>
                {statusColor === 'success' ? 'SMTP actif' : 'SMTP non configuré'}
              </p>
              <p className={`text-xs ${statusColor === 'success' ? 'text-emerald-700' : 'text-amber-700'}`}>
                {statusColor === 'success'
                  ? `Les emails partent depuis ${config.sender_email} via ${config.host}`
                  : 'Renseignez les champs ci-dessous et activez l\'envoi'}
              </p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={config.enabled}
                onChange={e => setConfig(c => ({ ...c, enabled: e.target.checked }))}
                className="sr-only peer"
                data-testid="toggle-enabled" />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500" />
            </label>
          </div>
        </div>

        {/* Quick presets */}
        <div className="bg-sky-50 border border-sky-100 rounded-xl p-4 mb-6" data-testid="presets-block">
          <div className="flex items-center gap-2 mb-2">
            <Info size={14} className="text-sky-600" />
            <p className="text-xs font-bold text-sky-900">Préréglages PlanetHoster courants</p>
          </div>
          <p className="text-xs text-sky-800 leading-relaxed mb-3">
            Récupérez les valeurs exactes dans <strong>Mon Cpanel → Comptes Email → Configurer le client de messagerie</strong>.
            Le serveur SMTP ressemble à <code className="px-1 py-0.5 bg-white rounded text-[11px]">mail.sfo-alc.fr</code> ou <code className="px-1 py-0.5 bg-white rounded text-[11px]">smtp-XX.n0c.com</code>.
          </p>
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={() => setPortPreset(465)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${config.port === 465 ? 'bg-sky-600 text-white' : 'bg-white text-sky-700 hover:bg-sky-100'}`}
              data-testid="preset-465">
              SSL/TLS — port 465 (recommandé)
            </button>
            <button type="button" onClick={() => setPortPreset(587)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${config.port === 587 ? 'bg-sky-600 text-white' : 'bg-white text-sky-700 hover:bg-sky-100'}`}
              data-testid="preset-587">
              STARTTLS — port 587
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6" data-testid="config-form">
          <h2 className="text-base font-bold text-[#1e3a5f] mb-5 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
            <Server size={18} className="text-[#35AFE4]" /> Serveur SMTP
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="sm:col-span-2">
              <Label>Serveur SMTP (host)</Label>
              <Input value={config.host}
                onChange={v => setConfig(c => ({ ...c, host: v }))}
                placeholder="mail.sfo-alc.fr"
                icon={Server}
                testId="input-host" />
            </div>
            <div>
              <Label>Port</Label>
              <input type="number" value={config.port}
                onChange={e => setPortPreset(parseInt(e.target.value || 0))}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                data-testid="input-port" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Identifiant SMTP</Label>
              <Input value={config.username}
                onChange={v => setConfig(c => ({ ...c, username: v }))}
                placeholder="noreply@sfo-alc.fr"
                icon={User}
                testId="input-username" />
            </div>
            <div>
              <Label>Mot de passe SMTP {config.has_password && !config.password && <span className="text-[10px] font-normal text-emerald-600">(conservé)</span>}</Label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={showPassword ? 'text' : 'password'}
                  value={config.password}
                  onChange={e => setConfig(c => ({ ...c, password: e.target.value }))}
                  placeholder={config.has_password ? '••••••••' : 'Mot de passe'}
                  className="w-full pl-9 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                  data-testid="input-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"
                  data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-4 flex-wrap text-xs">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={config.use_ssl}
                onChange={e => setConfig(c => ({ ...c, use_ssl: e.target.checked, use_starttls: e.target.checked ? false : c.use_starttls }))}
                className="accent-[#35AFE4]" data-testid="checkbox-ssl" />
              <span className="text-gray-700 font-medium">SSL/TLS (recommandé pour port 465)</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={config.use_starttls}
                onChange={e => setConfig(c => ({ ...c, use_starttls: e.target.checked, use_ssl: e.target.checked ? false : c.use_ssl }))}
                className="accent-[#35AFE4]" data-testid="checkbox-starttls" />
              <span className="text-gray-700 font-medium">STARTTLS (pour port 587)</span>
            </label>
          </div>

          <hr className="border-gray-100 my-5" />

          <h2 className="text-base font-bold text-[#1e3a5f] mb-4 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
            <Mail size={18} className="text-[#35AFE4]" /> Expéditeur affiché
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Email expéditeur (From)</Label>
              <Input value={config.sender_email}
                onChange={v => setConfig(c => ({ ...c, sender_email: v }))}
                placeholder="noreply@sfo-alc.fr"
                icon={Mail}
                testId="input-sender-email" />
            </div>
            <div>
              <Label>Nom expéditeur</Label>
              <Input value={config.sender_name}
                onChange={v => setConfig(c => ({ ...c, sender_name: v }))}
                placeholder="SFOALC"
                testId="input-sender-name" />
            </div>
          </div>
        </div>

        {/* Save */}
        {checkResult && (
          <div className={`mb-3 rounded-xl border px-4 py-3 text-sm flex items-start gap-2 ${checkResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}
            data-testid="smtp-check-result" data-ok={checkResult.ok ? 'true' : 'false'}>
            {checkResult.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
            <div>
              <p className="font-medium">{checkResult.message}</p>
              {checkResult.ok && !config.enabled && <p className="text-xs mt-1">L'envoi est encore désactivé : activez-le en haut de la page puis enregistrez.</p>}
              {checkResult.ok && config.password && <p className="text-xs mt-1">Pensez à enregistrer la configuration pour garder ce mot de passe.</p>}
              {checkResult.detail && <p className="text-[11px] opacity-70 mt-1 font-mono break-all">{checkResult.detail}</p>}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mb-8 flex-wrap">
          <button onClick={checkConnection} disabled={checking}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#1e3a5f]/20 text-[#1e3a5f] hover:bg-[#1e3a5f]/5 text-sm font-bold rounded-lg transition-colors disabled:opacity-50"
            data-testid="check-connection-btn">
            {checking ? <Loader2 size={14} className="animate-spin" /> : <Server size={14} />}
            Vérifier la connexion
          </button>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#35AFE4] hover:bg-[#1e9bd4] text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-50"
            data-testid="save-config-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Enregistrer la configuration
          </button>
        </div>

        {/* Test */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="test-block">
          <h2 className="text-base font-bold text-[#1e3a5f] mb-2 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
            <Send size={18} className="text-[#35AFE4]" /> Tester la configuration
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            Envoie un email "Bienvenue" de test à l'adresse de votre choix avec les paramètres actuellement sauvegardés.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input value={testEmail}
              onChange={setTestEmail}
              placeholder="votre@email.com"
              icon={Mail}
              testId="input-test-email"
              className="flex-1" />
            <button onClick={sendTest} disabled={testing}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#1e3a5f] hover:bg-[#162d4a] text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-50"
              data-testid="send-test-btn">
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Envoyer un test
            </button>
          </div>
          {!config.has_password && !config.password && (
            <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 rounded p-2 flex items-center gap-1.5">
              <AlertCircle size={12} /> Le mot de passe SMTP n'est pas encore défini.
            </p>
          )}
        </div>

        {/* === Email logs / Journal === */}
        <EmailLogs />

      </div>
    </AdminLayout>
  );
};

// =====================================================================
// Email logs section
// =====================================================================
const EmailLogs = () => {
  const [logs, setLogs] = useState({ items: [], total: 0, stats_7d: { sent: 0, failed: 0, skipped: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', search: '' });
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
        ...(filter.status ? { status: filter.status } : {}),
        ...(filter.search ? { search: filter.search } : {}),
      });
      const res = await fetch(`${API}/api/admin/email-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setLogs({
        items: data?.items || [],
        total: data?.total || 0,
        stats_7d: data?.stats_7d || { sent: 0, failed: 0, skipped: 0 },
      });
    } catch (e) {
      // silent
    }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, [page, filter.status]);

  const statusBadge = (s) => {
    const map = {
      sent: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Envoyé' },
      failed: { bg: 'bg-red-50', text: 'text-red-700', label: 'Échec' },
      skipped: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Ignoré' },
    }[s] || { bg: 'bg-gray-50', text: 'text-gray-700', label: s };
    return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${map.bg} ${map.text}`}>{map.label}</span>;
  };

  const templateLabel = (t) => {
    const map = {
      welcome: 'Bienvenue',
      payment_confirmation: 'Confirmation paiement',
      student_card_decision_approved: 'Carte étudiante ✓',
      student_card_decision_rejected: 'Carte étudiante ✗',
      student_card_expiry_reminder: 'Rappel expiration',
      monthly_digest: 'Digest mensuel',
    };
    return map[t] || (t || '—');
  };

  const totalPages = Math.ceil(logs.total / PAGE_SIZE);

  return (
    <div className="mt-8 bg-white rounded-2xl border border-gray-100 p-6" data-testid="email-logs-block">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-5">
        <div>
          <h2 className="text-base font-bold text-[#1e3a5f] flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
            <Inbox size={18} className="text-[#35AFE4]" /> Journal des emails
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Audit de tous les envois — utile pour confirmer la livraison.</p>
        </div>
        <button onClick={() => { setPage(0); fetchLogs(); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-bold rounded-lg transition-colors"
          data-testid="refresh-logs-btn">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <StatCard label="Envoyés (7j)" value={logs.stats_7d.sent} color="emerald" />
        <StatCard label="Échecs (7j)" value={logs.stats_7d.failed} color="red" />
        <StatCard label="Ignorés (7j)" value={logs.stats_7d.skipped} color="amber" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={filter.search}
            onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && (setPage(0), fetchLogs())}
            placeholder="Rechercher (email, sujet)…"
            className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#35AFE4]"
            data-testid="logs-search-input" />
        </div>
        <select value={filter.status}
          onChange={e => { setFilter(f => ({ ...f, status: e.target.value })); setPage(0); }}
          className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#35AFE4]"
          data-testid="logs-status-filter">
          <option value="">Tous statuts</option>
          <option value="sent">Envoyés uniquement</option>
          <option value="failed">Échecs uniquement</option>
          <option value="skipped">Ignorés uniquement</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="logs-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 font-bold">
              <th className="px-2 py-2.5">Date</th>
              <th className="px-2 py-2.5">Destinataire</th>
              <th className="px-2 py-2.5 hidden md:table-cell">Sujet</th>
              <th className="px-2 py-2.5 hidden lg:table-cell">Type</th>
              <th className="px-2 py-2.5 text-center">Statut</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-xs">
                <Loader2 size={16} className="animate-spin inline mr-1" /> Chargement…
              </td></tr>
            )}
            {!loading && logs.items.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-gray-400 text-xs">
                Aucun email envoyé pour le moment.
              </td></tr>
            )}
            {!loading && logs.items.map((log, i) => (
              <tr key={log.id || i}
                onClick={() => setSelected(log)}
                className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer text-[12px] text-gray-700"
                data-testid={`log-row-${i}`}>
                <td className="px-2 py-2.5 whitespace-nowrap text-gray-400 text-[11px]">
                  {new Date(log.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                </td>
                <td className="px-2 py-2.5 truncate max-w-[180px]" title={log.to_email}>{log.to_email}</td>
                <td className="px-2 py-2.5 truncate max-w-[260px] hidden md:table-cell" title={log.subject}>{log.subject}</td>
                <td className="px-2 py-2.5 hidden lg:table-cell text-[11px] text-gray-500">{templateLabel(log.template)}</td>
                <td className="px-2 py-2.5 text-center">{statusBadge(log.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-gray-500" data-testid="logs-pagination">
          <span>Page {page + 1} / {totalPages} — {logs.total} entrées</span>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1 rounded-md bg-gray-50 hover:bg-gray-100 disabled:opacity-40">Précédent</button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded-md bg-gray-50 hover:bg-gray-100 disabled:opacity-40">Suivant</button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="log-detail-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} className="absolute top-3 right-3 p-1 rounded-full bg-gray-100 hover:bg-gray-200" data-testid="log-detail-close">
              <X size={14} />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <Inbox size={18} className="text-[#35AFE4]" />
              <h3 className="text-base font-bold text-[#1e3a5f]">Détail de l'envoi</h3>
            </div>
            <dl className="space-y-2 text-sm">
              <Row label="Date"><span className="font-mono text-[12px]">{new Date(selected.created_at).toLocaleString('fr-FR')}</span></Row>
              <Row label="Destinataire"><span className="font-mono text-[12px]">{selected.to_email}</span></Row>
              <Row label="Sujet">{selected.subject}</Row>
              <Row label="Type">{templateLabel(selected.template)}</Row>
              <Row label="Statut">{statusBadge(selected.status)}</Row>
              {selected.error && (
                <div className="bg-red-50 border border-red-100 rounded-lg p-3 mt-2">
                  <p className="text-[10px] uppercase font-bold text-red-700 tracking-wider mb-1">Erreur</p>
                  <p className="text-[12px] text-red-800 font-mono break-all">{selected.error}</p>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => {
  const colors = {
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    red: 'bg-red-50 border-red-100 text-red-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[color]}`}>
      <p className="text-[10px] uppercase font-bold tracking-wider opacity-70">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
};

const Row = ({ label, children }) => (
  <div className="flex gap-2 text-sm">
    <dt className="text-[11px] uppercase tracking-wider text-gray-400 font-bold w-24 flex-shrink-0 pt-0.5">{label}</dt>
    <dd className="flex-1 text-gray-700">{children}</dd>
  </div>
);

// =====================================================================
// Helpers
// =====================================================================
const Label = ({ children }) => (
  <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">{children}</label>
);

const Input = ({ value, onChange, placeholder, icon: Icon, testId, className = '' }) => (
  <div className={`relative ${className}`}>
    {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full ${Icon ? 'pl-9' : 'pl-3'} pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none transition-all`}
      data-testid={testId}
    />
  </div>
);

export default EmailSettingsPage;
